import { useCallback, useEffect, useState } from "react";
import type { AttachmentDto } from "@grizzly/shared";
import { api, attachmentContentUrl } from "../lib/apiClient.js";
import type { ApiClientError } from "../lib/apiClient.js";
import { formatBytes, formatDateTime, formatSizeLabel } from "../lib/format.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { PageHeader } from "../components/ui/card.js";
import { DataTable, type Column } from "../components/ui/dataTable.js";
import { OwnerFilter } from "../components/documents/ownerFilter.js";
import { UploadCard } from "../components/documents/uploadCard.js";
import { DeleteAttachmentDialog } from "../components/documents/deleteAttachmentDialog.js";
import { useHasPermission } from "../lib/session.js";
import { useI18n } from "../i18n/index.js";
import { Download, Trash2 } from "lucide-react";

interface ListState {
  loading: boolean;
  error: { message: string; requestId?: string } | null;
  items: AttachmentDto[];
}

export function DocumentsPage() {
  const { t } = useI18n();
  const canWrite = useHasPermission("attachments:write");
  const canDelete = useHasPermission("attachments:delete");
  const [ownerType, setOwnerType] = useState("other");
  const [ownerId, setOwnerId] = useState("shop");
  const [list, setList] = useState<ListState>({ loading: true, error: null, items: [] });
  const [pendingDelete, setPendingDelete] = useState<AttachmentDto | null>(null);

  const load = useCallback(async () => {
    setList((s) => ({ ...s, loading: true }));
    try {
      const res = await api.listAttachments(ownerType, ownerId);
      setList({ loading: false, error: null, items: res.items });
    } catch (err) {
      const e = err as ApiClientError;
      setList({
        loading: false,
        error: { message: e.message, requestId: e.requestId },
        items: [],
      });
    }
  }, [ownerType, ownerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: Column<AttachmentDto>[] = [
    {
      key: "name",
      header: t("documents.column.document"),
      render: (row) => (
        <span className="font-medium text-ink">
          {row.originalName}
          <span className="block text-xs font-normal text-ink-muted">
            {t("documents.uploaded")} {formatDateTime(row.createdAt)}
            {row.uploadedByName ? ` ${t("documents.by")} ${row.uploadedByName}` : ""}
          </span>
        </span>
      ),
    },
    {
      key: "type",
      header: t("common.fields.type"),
      render: (row) => <Badge tone="neutral">{formatSizeLabel(row.contentType)}</Badge>,
    },
    {
      key: "size",
      header: t("documents.column.size"),
      render: (row) => <span className="tabular">{formatBytes(row.sizeBytes)}</span>,
    },
    {
      key: "actions",
      header: t("common.actions"),
      render: (row) => (
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" asChild>
            <a href={attachmentContentUrl(row.id)}>
              <Download className="h-4 w-4" aria-hidden="true" />
              {t("documents.download")}
            </a>
          </Button>
          {canDelete ? (
            <Button size="sm" variant="danger" onClick={() => setPendingDelete(row)}>
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              {t("common.delete")}
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader title={t("nav.documents")} description={t("documents.description")} />

      <OwnerFilter
        ownerType={ownerType}
        ownerId={ownerId}
        onOwnerTypeChange={setOwnerType}
        onOwnerIdChange={setOwnerId}
      />

      {canWrite ? (
        <UploadCard ownerType={ownerType} ownerId={ownerId} onUploaded={load} />
      ) : null}

      <DataTable
        caption={t("documents.caption", { ownerType, ownerId })}
        loading={list.loading}
        error={list.error}
        onRetry={load}
        rows={list.items}
        rowKey={(row) => row.id}
        emptyTitle={t("documents.emptyTitle")}
        emptyDescription={t("documents.emptyDescription")}
        columns={columns}
      />

      <DeleteAttachmentDialog
        target={pendingDelete}
        onClose={() => setPendingDelete(null)}
        onDeleted={load}
      />
    </>
  );
}
